import { createClient, SupabaseClient } from '@supabase/supabase-js'
import type { TenantSchema } from '@dcp/shared/types/tenant-database'
import { SUPABASE_STORAGE_KEY } from './supabase'
import { getSupabaseUrl, getSupabaseAnonKey } from './runtime-env'
import { UUID_REGEX } from '@dcp/shared/utils/validators'

/**
 * The tenant schema type extracted from TenantDatabase.
 * We map the dynamic tenant schema to "public" key so Supabase's type system
 * resolves Row/Insert/Update types correctly for all tenant tables.
 */
type TenantSchemaDb = {
  public: TenantSchema
}

/**
 * Typed tenant Supabase client.
 * Uses TenantSchemaDb where tenant tables are mapped to the "public" key.
 * At runtime, the actual schema (e.g., tenant_abc123) is set via db.schema option.
 */
export type TenantSupabaseClient = SupabaseClient<TenantSchemaDb>

/**
 * Convert organization UUID to tenant schema name.
 * Validates UUID format to prevent invalid schema names.
 * Format: tenant_<uuid_without_dashes>
 * @param orgId - Organization UUID (e.g., "a1b2c3d4-e5f6-7890-abcd-ef1234567890")
 * @returns Schema name (e.g., "tenant_a1b2c3d4e5f67890abcdef1234567890")
 * @throws Error if orgId is not a valid UUID
 */
export function getTenantSchemaName(orgId: string): string {
  if (!UUID_REGEX.test(orgId)) {
    throw new Error(`Invalid organization ID format: ${orgId}`)
  }
  return `tenant_${orgId.replace(/-/g, '')}`
}

/** Cache of tenant Supabase clients keyed by schema name */
const tenantClients = new Map<string, TenantSupabaseClient>()

/**
 * Get or create a Supabase client configured for a specific tenant schema.
 * Clients are cached and share auth session with the main public client.
 * @param orgId - Organization UUID to create tenant client for
 * @returns Supabase client configured for the tenant schema
 */
export function getTenantClient(orgId: string): TenantSupabaseClient {
  const schemaName = getTenantSchemaName(orgId)

  const existing = tenantClients.get(schemaName)
  if (existing) return existing

  // Читаем runtime-конфиг лениво (значения приходят из window.__ENV__/config.js).
  // getSupabase* бросают при отсутствии конфига — нормализуем в прежнее
  // сообщение 'Supabase not configured' для обратной совместимости.
  let supabaseUrl: string
  let supabaseAnonKey: string
  try {
    supabaseUrl = getSupabaseUrl()
    supabaseAnonKey = getSupabaseAnonKey()
  } catch {
    throw new Error('Supabase not configured')
  }

  // Create client with tenant schema. The schema name is dynamic (tenant_<uuid>),
  // but TenantSchemaDb maps all tables under the "public" key for type resolution.
  // We cast schema to "public" for the type system, while the actual schema name
  // is passed at runtime to Supabase's PostgREST.
  const client = createClient<TenantSchemaDb>(supabaseUrl, supabaseAnonKey, {
    db: { schema: schemaName as 'public' },
    auth: {
      // Token refresh is handled by the main public client (supabase.ts).
      // Tenant clients share storageKey, so refreshed tokens propagate automatically.
      autoRefreshToken: false,
      persistSession: true,
      detectSessionInUrl: false,
      storageKey: SUPABASE_STORAGE_KEY,
    },
  })

  tenantClients.set(schemaName, client as TenantSupabaseClient)
  return client as TenantSupabaseClient
}

/**
 * Clear all cached tenant clients.
 * Should be called on sign out to prevent stale sessions.
 */
export function clearTenantClients(): void {
  tenantClients.clear()
}
